/**
 * this component will be landing page for customer when login or when book appoitnment to particular store 
 * it show all available stores 
 * it search store and sort store and also show store based on mutli selet business type 
 * created by Sahil Bharti on 11-03-2025
 */

import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../contexts/store-provider';
import { observer } from 'mobx-react';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { formatMessage } from '../../../translations/format-message';
import IPageInfo, { IOption, IPageLimitInfo } from '../../../models/ICommon';
import userSettingEnum from '../../../constants/enums/user-setting-enum';
import Pagination from '../../../shared-components/Grid/Pagination';
import { ISorting } from '../../../models/ISorting';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

const Home: React.FC = () => {
  const { storeManagementStore, preferencesStore, masterManagementStore } = useStore();
  const {
    inProgress,
    GetStoreManagementListService,
    GetStoreManagementFreewordFilterService,
    storeList,
    getStoreFilter,
    GetStoreRegistrationByBusinessTypeIdService
  } = storeManagementStore;
  const {
    GetAllBusinessTypeService,
    getAllBusinessTypesAll,
    GetReservationSettingByIdService,
  } = masterManagementStore;
  const { setUserSetting } = preferencesStore;

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<number[]>([]);

  const pageLimitOptions: Array<number> = [10, 50, 100];

  const [pageLimitInfo, setPageLimitInfo] = useState<IPageLimitInfo>({
    pageLimit: 5,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  useEffect(() => {
    GetStoreManagementListService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      'Name',
      'asc',
      {}
    );
  }, [pageInfo.currentPage, pageLimitInfo.pageLimit]);

  useEffect(() => {
    if (storeList?.PagingDetails?.TotalRecords) {
      setPageInfo((prev) => ({
        ...prev,
        totalPages: Math.ceil(storeList.PagingDetails.TotalRecords / pageLimitInfo.pageLimit),
      }));
    }
  }, [storeList?.PagingDetails?.TotalRecords, pageLimitInfo.pageLimit]);

  const onPageChanged = (pageNumber: number) => {
    setPageInfo((prev) => ({ ...prev, currentPage: pageNumber, isPagerChange: true }));
  };

  const setIsRefreshPage = () => {
    setPageInfo((prev) => ({ ...prev, isRefreshPage: false }));
  };

  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setPageInfo({ currentPage: 1, totalPages: pageInfo.totalPages, isPagerChange: false, isRefreshPage: false });
    setPageLimitInfo({ pageLimit: parseInt(evt.target.value), isPageLimitChange: true });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const handleSearch = () => {
    if (searchText.trim() !== '') {
      GetStoreManagementFreewordFilterService(1, pageLimitInfo.pageLimit, searchText);
    } else {
      GetStoreManagementListService(1, pageLimitInfo.pageLimit, 'Name', 'asc', {});
    }
  };

  const [sorting, setSorting] = useState<ISorting>({
    column: "store_name",
    orderBy: "asc",
  });

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;

    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    } else {
      sortOrder = "asc";
    }

    const updatedSorting = { column: accessor, orderBy: sortOrder };
    setSorting(updatedSorting);
    GetStoreManagementListService(pageInfo.currentPage, pageLimitInfo.pageLimit, updatedSorting.column, updatedSorting.orderBy, {});
  };

// fetch all business types 

  useEffect(() => {
    GetAllBusinessTypeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Handles checkbox selection */
  const handleBusinessTypeChange = (id: number) => {
    setSelectedBusinessTypes((prev) => {
      const updatedSelection = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];

      // if no business type is selected, fetch all stores
      if (updatedSelection.length === 0) {
        GetStoreManagementListService(pageInfo.currentPage, pageLimitInfo.pageLimit, "Name", "asc", {});
      } else {
        GetStoreRegistrationByBusinessTypeIdService(pageInfo.currentPage, pageLimitInfo.pageLimit, updatedSelection);
      }

      return updatedSelection;
    });
  };


  const stores = storeList?.StoreVM || [];

  return (
    <div className="container-fluid mt-5">
      {/* Header */}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-12">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="Home" />
              </h1>
            </div>
          </div>
        </div>
      </div>


      {/* Store Count & Search Bar */}
      <div className="container mt-5" style={{ paddingTop: '70px' }}>
        <div className="row align-items-center justify-content-center">
          {/* Total Stores Count */}
          <div className="col-auto">
            <p className="mb-0">
              {formatMessage('showing')} {stores.length} {formatMessage('out_of')} {storeList?.PagingDetails?.TotalRecords || stores.length} {formatMessage('stores_available')}
            </p>
          </div>

          {/* Sorting stores by store name  */}
          <div className="col-auto">
            <button className="btn btn-light" onClick={() => handleSortingChange('store_name')}>
              {formatMessage('store_name')}
              <FontAwesomeIcon icon={sorting.column === 'store_name' ? (sorting.orderBy === 'asc' ? faSortUp : faSortDown) : faSort} className="ms-2" />
            </button>
          </div>
          {/* Search Bar */}
          <div className="col-auto">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={formatMessage('search_by_free_word')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-primary" onClick={handleSearch}>
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Business Type Filter (Checkbox) */}
      <div className="container mt-4">
        <h4>{formatMessage("business_type")}</h4>
        <div className="row">
          {getAllBusinessTypesAll
            ?.filter((type: IOption) => type.id !== -1)
            .map((type: IOption) => (
              <div key={type.id} className="col-auto">
                <input
                  type="checkbox"
                  id={`businessType_${type.id}`}
                  checked={selectedBusinessTypes.includes(type.id)}
                  onChange={() => handleBusinessTypeChange(type.id)}
                />
                <label htmlFor={`businessType_${type.id}`} className="ms-2">
                  {type.value}
                </label>
              </div>
            ))}
        </div>
      </div>




      {/* Store Cards */}
      <div className="container mt-4">
        <FormLoader loading={inProgress} />
        {!inProgress && stores.length > 0 ? (
          <div className="row">
            {stores.map((store: any) => (
              <div key={store.Id} className="col-md-4 col-sm-6 mb-4">
                <div className="card shadow-sm p-3">
                  <img
                    src={store.Image || ''}
                    alt={store.StoreName}
                    className="card-img-top rounded"
                  />
                  <div className="card-body">
                    <h5 className="card-title mt-2">{formatMessage("store_name")}: {store.StoreName}</h5>
                    <p className="card-text">{formatMessage("address")}: {store.Address || 'N/A'}</p>
                    <button className="btn btn-primary mt-2">{formatMessage("book_now")}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !inProgress ? (
          <p className="text-center">{formatMessage("no_store_available")}</p>
        ) : null}
      </div>

      {/* Pagination */}
      {stores.length > 0 && (
        <div className="row no-gutters p-2 tableFooter">
          <div className="col-6 pt-1">
            <FormattedMessage id="show" />
            <select
              data-testid="pageSelect"
              className="form-select w-auto d-inline-block ms-2"
              onChange={onPageLimitChanged}
              value={pageLimitInfo.pageLimit}
            >
              {pageLimitOptions.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 d-flex justify-content-end">
            <Pagination
              isRefreshPage={pageInfo.isRefreshPage}
              totalPages={pageInfo.totalPages}
              pageLimit={pageLimitInfo.pageLimit}
              onPageChanged={onPageChanged}
              disablePagination={disablePagination}
              setIsRefreshPage={setIsRefreshPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(Home);
